import { type Category, type Car, type Brand } from '@prisma/client'
import { dateToDisplayUTC } from '#app/shared/lib/strings.js'
import { Separator } from '#app/shared/ui/data-display/Separator.js'
import { Spacer } from '#app/shared/ui/data-display/Spacer.js'
import CarSpecsCard from './carSpecsCard'
import CircleSpec from './circleSpec'

type Props = {
  id: string
  car: Car & { category: Category; brand: Brand }
}

export default function CarDetails({ id, car }: Props) {
  const details = [
    {
      imagePath: '/img/detail-icons/calendar-check-02.svg',
      name: 'AÑO',
      value: car.matriculation
        ? car.matriculation.getFullYear().toString()
        : car.year.toString(),
    },
    {
      imagePath: '/img/detail-icons/speedometer-01.svg',
      name: 'KILOMETRAJE',
      value: car.mileage.toLocaleString(),
    },
    {
      imagePath: '/img/detail-icons/calendar-date.svg',
      name: 'MATRICULADO',
      value: dateToDisplayUTC(car.matriculation) || '',
    },
    {
      imagePath: '/img/detail-icons/shield-03.svg',
      name: 'GARANTÍA DE FÁBRICA',
      value:
        (car.manufacturerMechanicalWarranty &&
          car.manufacturerMechanicalWarrantyExpiration) ||
        (car.manufacturerBatteryWarranty &&
          car.manufacturerBatteryWarrantyExpiration)
          ? 'Sí'
          : 'No',
    },
    {
      imagePath: '/img/detail-icons/Frame 172.svg',
      name: 'TRANSMISIÓN',
      value: car.transmission,
    },
    {
      imagePath: '/img/detail-icons/calendar-check-02 (1).svg',
      name: 'POTENCIA',
      value: `${car.horsePower.toString()} CV`,
    },
    {
      imagePath: '/img/detail-icons/Frame 172 (1).svg',
      name: 'COMBUSTIBLE',
      value: car.fuelType,
    },
    {
      imagePath: '/img/detail-icons/calendar-check-02 (2).svg',
      name: 'TRACCIÓN',
      value: car.traction,
    },
    {
      imagePath: '/img/detail-icons/calendar-check-02 (3).svg',
      name: 'PLAZAS',
      value: `${car.seats.toString()} plazas`,
    },
    {
      imagePath: '/img/detail-icons/calendar-check-02 (4).svg',
      name: 'PUERTAS',
      value: car.doors.toString(),
    },
    {
      imagePath: '/img/detail-icons/car-01.svg',
      name: 'CATEGORÍA',
      value: car.category.name.toString(),
    },
    {
      imagePath: '/img/detail-icons/box.svg',
      name: 'CAPACIDAD',
      value: car.maxTrunkCapacity
        ? `${car.trunkCapacity.toLocaleString('es-ES')} a ${car.maxTrunkCapacity.toLocaleString('es-ES')}l`
        : `${car.trunkCapacity.toLocaleString('es-ES')}l`,
    },
  ]

  const circleDetails = [
    {
      title: 'VELOCIDAD MÁXIMA',
      value: car.maximunSpeed.toString(),
      unit: 'km/h',
      max: 240,
      min: 120,
      delay: 0,
    },
    {
      title: 'DE 0 A 100 KM/H',
      value: car.acceleration.toFixed(1),
      unit: 'SEG.',
      max: 12,
      min: 4,
      delay: 200,
    },
    {
      title: 'CONSUMO MEDIO',
      value: car.averageConsumtion.toFixed(1),
      unit: '/100km',
      max: 9,
      min: 3,
      delay: 400,
    },
    {
      title: 'EMISIONES',
      value: car.emissions.toString(),
      unit: 'CO2 (g/km)',
      max: 320,
      min: 0,
      delay: 500,
    },
  ]

  return (
    <div
      className={`
        bg-white px-4

        md:px-7
      `}
      id={id}
    >
      <Spacer size="56px" />
      <p className="text-h4 text-neutral-900">
        {car.brand.name} {car.model}
      </p>
      <Spacer size="24px" />
      <Separator className="h-[2px] bg-neutral-75" />
      <Spacer size="12px" />
      <div
        className={`
          grid grid-cols-2 grid-rows-3 gap-x-7 gap-y-2

          md:grid-cols-4
        `}
      >
        {details.map((detail) => (
          <CarSpecsCard
            key={detail.name}
            imagePath={detail.imagePath}
            name={detail.name}
            value={detail.value}
          />
        ))}
      </div>
      <Spacer size="24px" />
      <div>
        <div
          className={`
            grid grid-cols-2 items-center justify-center rounded-base border
            border-neutral-75

            md:grid-cols-4
          `}
        >
          {circleDetails.map((details, i) => (
            <CircleSpec
              key={details.title}
              className={`
                ${i < 2 ? 'border-b' : ''}
                ${i % 2 === 0 ? 'border-r' : ''}
                ${
                  i === 1
                    ? `
                      border-r-0

                      md:border-r
                    `
                    : ''
                }

                border-neutral-75
              `}
              title={details.title}
              unit={details.unit}
              value={details.value}
              max={details.max}
              min={details.min}
              delay={details.delay}
            />
          ))}
        </div>
      </div>
    </div>
  )
}
